import merge from 'lodash.merge'
import { PopulatedAssessment } from '../../types/app/populated'
import { AssessmentForm } from '../../types/assessments/form'
import { AssessmentFormAlias, AssessmentFormKey } from '../../types/users/user'

import { AssessmentFormSnippet } from '../../types/forms/form'
import {
  defaultStageValidate,
  getFieldFromPath,
  getNestedFields,
  makeAllFieldsAdmin,
} from '../../utils/fields'
import { getFormStatus } from '../../utils/forms'
import { getCallInForm, optInCallInForm } from '../callIn'
import { getIntroductionForm } from '../introduction'
import { signOnFields } from '../signOn'

export const getAssessmentField = (
  admin: boolean,
  id: AssessmentFormKey,
  assessment: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
): AssessmentForm | AssessmentFormAlias => {
  switch (id) {
    case 'introduction':
      return getIntroductionForm(admin, assessment)
    case 'call-in':
      return getCallInForm(admin, assessment) ?? optInCallInForm
    case 'not-pregnant':
      return 'notPregnant'
    case 'plan-options':
      return 'planOptions'
    case 'schedule-call':
      return 'scheduleCall'
    case 'no-coverage':
      return 'noCoverage'
  }
}

export const getAssessmentFields = (
  admin: boolean,
  assessment: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
): Record<string, string | AssessmentForm> => {
  let currKey: AssessmentFormKey | null = 'introduction'
  let res: Record<string, AssessmentForm | string> = {}
  while (currKey) {
    const curr: AssessmentForm | string = getAssessmentField(admin, currKey, assessment)
    if (curr) {
      res = { ...res, [currKey]: curr }
      if (curr && typeof curr !== 'string' && curr.getNextFormId) {
        currKey = curr.getNextFormId(admin, assessment)
      } else {
        currKey = null
      }
    } else currKey = null
  }
  return res
}
export const getNestedAssessmentFields = (
  admin: boolean,
  assessment: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
) =>
  getNestedFields({
    ...getAssessmentFields(admin, assessment),
  })

export const getAssessmentFieldFromPath = (
  admin: boolean,
  assessment: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
  path: string,
) =>
  getFieldFromPath(
    {
      ...getNestedAssessmentFields(admin, assessment),
      children: {
        ...getNestedAssessmentFields(admin, assessment).children,
        signOn: signOnFields,
      },
    },
    path,
  )

export const validateAssessmentAnswers = (admin: boolean, assessment: PopulatedAssessment) => {
  const validated = {}
  merge(validated, assessment.data ?? {})
  if (admin) merge(validated, assessment.corrections ?? {})
  const nestedFields = getNestedFields(getAssessmentFields(admin, assessment))
  return defaultStageValidate(admin ? makeAllFieldsAdmin(nestedFields) : nestedFields)(validated)
}

export const getAvailableForms = (admin: boolean, assessment: PopulatedAssessment | null) => {
  const forms: AssessmentFormSnippet[] = []
  let currentFormId: AssessmentFormKey | null = 'introduction'
  while (currentFormId) {
    const form: AssessmentForm | AssessmentFormAlias | undefined = getAssessmentField(
      admin,
      currentFormId,
      assessment,
    )
    // const option = user?.insuranceInfo?.data?.option
    const formData = assessment?.data ?? {}
    if (form) {
      if (typeof form !== 'string') {
        const status = getFormStatus(form, formData)
        forms.push({ id: currentFormId, status })
        if (status !== 'complete') {
          currentFormId = null
        } else if (form.getNextFormId) {
          currentFormId = form.getNextFormId(admin, assessment)
        } else {
          currentFormId = null
        }
      } else {
        forms.push({ id: currentFormId, status: 'info' })
        currentFormId = null
      }
    } else if (currentFormId) {
      forms.push({ id: currentFormId, status: 'info' })
      currentFormId = null
    } else {
      console.error(`Can't find form with id: ${currentFormId}`)
      currentFormId = null
    }
  }

  return forms
}
